/** Synchronous field validation shared by designer preview and form submission. */
import type { FieldValidation, FormField } from '../models/form';
import { parseNumericValue, toEnglishNumbers } from './numbers';
import { isLegacyMaskComplete } from './mask';
import { isValidHttpUrl, isValidIranianLandline, isValidIranianMobile } from './input-normalization';
import { parseJalaliDate } from './persian-date';
import { isMultipleFileField, parseFileValues } from './file-values';
import { isIranianCardNumber, isIranianIban, isIranianPostalCode } from './input-presets';
import { normalizeRegexDefinition, runtimeRegexFlags } from '../legacy/regex-literal';
import type { UiLocale } from '../app/ui-locale';
import { publicCopy } from '../features/renderer/public-copy';

export interface ValidationError {
  key: string;
  message: string;
}

/** Validates the Iranian national-code checksum after digit normalization. */
export function isValidIranianNationalCode(raw: unknown): boolean {
  const input = toEnglishNumbers(raw);
  if (!/^\d{10}$/.test(input) || /^(\d)\1{9}$/.test(input)) return false;
  const check = Number(input[9]);
  const sum = input.slice(0, 9).split('').reduce((total, digit, index) => total + Number(digit) * (10 - index), 0) % 11;
  return (sum < 2 && check === sum) || (sum >= 2 && check + sum === 11);
}


function inputTypeError(field: FormField, rawValue: string, locale: UiLocale): ValidationError | undefined {
  const copy = publicCopy(locale);
  if (field.type !== 'input') return undefined;
  const value = toEnglishNumbers(rawValue).trim();
  switch (field.inputType) {
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? undefined
        : { key: 'type', message: copy.invalidEmail };
    case 'tel':
      if (field.inputPreset === 'iranLandline') {
        return isValidIranianLandline(value)
          ? undefined
          : { key: 'type', message: copy.invalidLandline };
      }
      return isValidIranianMobile(value)
        ? undefined
        : { key: 'type', message: copy.invalidMobile };
    case 'url':
      return isValidHttpUrl(value)
        ? undefined
        : { key: 'type', message: copy.invalidUrl };
    case 'number':
      return parseNumericValue(value) !== undefined
        ? undefined
        : { key: 'type', message: copy.invalidNumber };
    case 'tdate':
      return parseJalaliDate(value)
        ? undefined
        : { key: 'type', message: copy.invalidJalaliDate };
    case 'ttime':
      return /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(value)
        ? undefined
        : { key: 'type', message: copy.invalidTime };
    case 'tdatetime': {
      const parts = value.split(/\s+/);
      const validDate = parseJalaliDate(parts[0] ?? '');
      const validTime = /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(parts.slice(1).join(' '));
      return validDate && validTime
        ? undefined
        : { key: 'type', message: copy.invalidJalaliDateTime };
    }
    default:
      return undefined;
  }
}

function createRegex(raw: string, explicitFlags?: string): RegExp {
  const normalized = normalizeRegexDefinition(raw, explicitFlags ?? '');
  return new RegExp(normalized.pattern, runtimeRegexFlags(normalized.flags));
}

/** Applies all synchronous validation rules and returns localized errors. */
export function validateFieldSync(field: FormField, value: unknown, locale: UiLocale = 'fa'): ValidationError[] {
  const copy = publicCopy(locale);
  const validation: FieldValidation = field.validation ?? {};
  const errors: ValidationError[] = [];
  const stringValue = typeof value === 'string' ? value : value == null ? '' : String(value);
  const multipleFiles = field.type === 'fileupload' ? isMultipleFileField(field) : false;
  const fileValues = field.type === 'fileupload' ? parseFileValues(value, multipleFiles) : [];
  const isUncheckedCheckbox = field.type === 'checkbox' && !(value === true || stringValue.toLowerCase() === 'true');
  const isEmpty = field.type === 'fileupload'
    ? fileValues.length === 0
    : value == null || stringValue.trim() === '' || (Array.isArray(value) && value.length === 0);

  if (validation.required && (isEmpty || isUncheckedCheckbox)) {
    errors.push({ key: 'required', message: validation.requiredMessage || copy.required });
  }
  if (field.type === 'table' && validation.minimumRows != null) {
    const rowCount = Array.isArray(value) ? value.length : 0;
    if (rowCount < validation.minimumRows) {
      errors.push({ key: 'minimumRows', message: validation.minimumRowsMessage || copy.minimumRows(validation.minimumRows) });
    }
  }
  if (field.type === 'table' && validation.maximumRows != null) {
    const rowCount = Array.isArray(value) ? value.length : 0;
    if (rowCount > validation.maximumRows) {
      errors.push({ key: 'maximumRows', message: validation.maximumRowsMessage || copy.maximumRowsAllowed(validation.maximumRows) });
    }
  }
  if (field.type === 'fileupload' && multipleFiles) {
    if (validation.minimumFiles != null && fileValues.length < validation.minimumFiles) {
      errors.push({ key: 'minimumFiles', message: validation.minimumFilesMessage || copy.minimumFiles(validation.minimumFiles) });
    }
    if (validation.maximumFiles != null && fileValues.length > validation.maximumFiles) {
      errors.push({ key: 'maximumFiles', message: validation.maximumFilesMessage || copy.maximumFilesAllowed(validation.maximumFiles) });
    }
  }
  if (isEmpty || isUncheckedCheckbox) return errors;

  const typeError = inputTypeError(field, stringValue, locale);
  if (typeError) errors.push(typeError);

  if (field.type === 'input' && field.inputType === 'number' && !typeError) {
    const numericValue = parseNumericValue(stringValue);
    if (numericValue != null) {
      if (validation.minValue != null && numericValue < validation.minValue) {
        errors.push({ key: 'minValue', message: validation.minValueMessage || copy.minimumValue(validation.minValue) });
      }
      if (validation.maxValue != null && numericValue > validation.maxValue) {
        errors.push({ key: 'maxValue', message: validation.maxValueMessage || copy.maximumValue(validation.maxValue) });
      }
      if (validation.step != null && validation.step > 0) {
        const origin = validation.minValue ?? 0;
        const ratio = (numericValue - origin) / validation.step;
        const tolerance = 1e-9 * Math.max(1, Math.abs(ratio));
        if (Math.abs(ratio - Math.round(ratio)) > tolerance) {
          errors.push({ key: 'step', message: validation.stepMessage || copy.numericStep(validation.step) });
        }
      }
    }
  }

  // The server challenge defines captcha length/mode. Legacy forms may still
  // carry ng-minlength metadata, which must not reject math-mode answers.
  if (field.type !== 'simplecaptcha' && validation.minLength != null && stringValue.length < validation.minLength) {
    errors.push({ key: 'minLength', message: validation.minLengthMessage || copy.minimumLength(validation.minLength) });
  }
  if (field.type !== 'simplecaptcha' && validation.maxLength != null && stringValue.length > validation.maxLength) {
    errors.push({ key: 'maxLength', message: validation.maxLengthMessage || copy.maximumLength(validation.maxLength) });
  }
  if (validation.mask && !isLegacyMaskComplete(stringValue, validation.mask)) {
    errors.push({ key: 'mask', message: validation.maskMessage || copy.maskMismatch });
  }
  if (validation.regex) {
    try {
      if (!createRegex(validation.regex, validation.regexFlags).test(stringValue)) errors.push({ key: 'regex', message: validation.regexMessage || copy.invalidValue });
    } catch {
      errors.push({ key: 'regex', message: copy.invalidValidationPattern });
    }
  }
  if (validation.custom === 'iranianNationalCode' && !isValidIranianNationalCode(stringValue)) {
    errors.push({ key: 'custom', message: validation.customMessage || copy.invalidNationalCode });
  }
  if (validation.custom === 'iranianLandline' && !isValidIranianLandline(stringValue)) {
    errors.push({ key: 'custom', message: validation.customMessage || copy.invalidLandline });
  }
  if (validation.custom === 'iranianIban' && !isIranianIban(stringValue)) {
    errors.push({ key: 'custom', message: validation.customMessage || copy.invalidIban });
  }
  if (validation.custom === 'iranianPostalCode' && !isIranianPostalCode(stringValue)) {
    errors.push({ key: 'custom', message: validation.customMessage || copy.invalidPostalCode });
  }
  if (validation.custom === 'iranianCardNumber' && !isIranianCardNumber(stringValue)) {
    errors.push({ key: 'custom', message: validation.customMessage || copy.invalidCardNumber });
  }
  if (validation.custom === 'numeric' && parseNumericValue(stringValue) === undefined) {
    errors.push({ key: 'custom', message: validation.customMessage || copy.numericOnly });
  }
  return errors;
}
